// src/components/ScanButton.jsx
//
// Универсальная кнопка-иконка камеры для запуска сканера.
//
// Использование:
//   <ScanButton onScan={(barcode) => { ... }} />
//
// onScan получает уже распарсенный баркод (GTIN или EAN),
// либо ничего не вызывается если юзер отменил сканирование.

import { useScanner } from '../hooks/useScanner';
import { useHardwareScan } from '../hooks/useHardwareScan';
import { parseBarcode } from '../utils/parseBarcode';
import Icon from './Icon';

export default function ScanButton({
  onScan,
  size = 'md',         // 'sm' | 'md' | 'lg'
  variant = 'icon',    // 'icon' (кнопка-кружок) | 'button' (с текстом)
  label,               // текст для variant='button'
  style = {},
  disabled = false,
  raw = false,         // true — отдать сырой код сканера (с серийником), без parseBarcode
}) {
  const { scan, busy } = useScanner();

  // Общая обработка сырого кода: и для камеры, и для железного сканера.
  const handleRaw = (rawValue) => {
    if (!rawValue) return;
    if (raw) { onScan(rawValue); return; }
    const code = parseBarcode(rawValue);
    if (code) onScan(code);
  };

  const handleClick = async () => {
    const rawValue = await scan();
    handleRaw(rawValue);
  };

  // Беспроводной HID-сканер → та же логика, пока кнопка на экране и активна.
  useHardwareScan(handleRaw, !disabled);

  const sizePx = size === 'sm' ? 32 : size === 'lg' ? 48 : 40;
  const iconSize = size === 'sm' ? 16 : size === 'lg' ? 22 : 18;

  if (variant === 'button') {
    return (
      <button
        onClick={handleClick}
        disabled={disabled || busy}
        className="btn btn-secondary"
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          ...style,
        }}
      >
        <Icon name="camera" size={iconSize} />
        <span>{label || 'Сканировать'}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={disabled || busy}
      title="Сканировать"
      style={{
        width: sizePx, height: sizePx,
        padding: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-sm)',
        color: 'var(--brand-primary)',
        cursor: busy ? 'wait' : 'pointer',
        flexShrink: 0,
        ...style,
      }}
    >
      <Icon name="camera" size={iconSize} />
    </button>
  );
}
